// src/pages/Admin/Users/UserModal.jsx
import React, { useEffect, useState } from "react";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/Modal/Modal";

/* Rôles disponibles (cohérents avec getRoleName côté liste) */
const ROLES = [
  { id: 1, label: "Administrateur" },
  { id: 2, label: "Utilisateur" },
  { id: 3, label: "Dirigeant Club" },
  { id: 4, label: "Arbitrage" },
  { id: 5, label: "Responsable Arbitrage" },
  { id: 6, label: "Membre Ligue" },
  { id: 7, label: "Responsable Comité" },
  { id: 8, label: "Enseignant-Coach" },
];

const emailOk = (v = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

export default function UserModal({ isOpen, onClose, onSubmit, user }) {
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const demoMode = userInfo?.role_id === 2; // Mode démo

  useEffect(() => {
    if (!isOpen) return; // évite de peupler quand fermé
    if (user) {
      setNom(user.nom || "");
      setPrenom(user.prenom || "");
      setEmail(user.email || "");
      setRoleId(user.role_id ?? "");
    } else {
      setNom("");
      setPrenom("");
      setEmail("");
      setRoleId("");
    }
    setErrors({});
    setSaving(false);
  }, [isOpen, user]);

  const validate = () => {
    const e = {};
    if (!nom.trim()) e.nom = "Le nom est requis.";
    if (!prenom.trim()) e.prenom = "Le prénom est requis.";
    if (!email.trim()) e.email = "L'email est requis.";
    else if (!emailOk(email)) e.email = "Email invalide.";
    if (!String(roleId)) e.roleId = "Le rôle est requis.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (demoMode) return; // bouton déjà désactivé visuellement

    if (!validate()) return;
    try {
      setSaving(true);
      await onSubmit({
        nom: nom.trim(),
        prenom: prenom.trim(),
        email: email.trim(),
        role_id: parseInt(roleId, 10),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="user-modal-title"
      describedById="user-modal-desc"
      initialFocusSelector="#user-nom"
    >
      <ModalHeader
        id="user-modal-title"
        title={user ? "Modifier un utilisateur" : "Créer un utilisateur"}
        subtitle={user ? `Utilisateur #${user.id ?? ""}` : "Renseignez les informations de l’utilisateur"}
        onClose={onClose}
      />

      <ModalBody id="user-modal-desc">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Aide contextuelle */}
          <div className="text-xs text-slate-600 dark:text-slate-300">
            Les champs marqués d’un <span className="text-pink-600">*</span> sont obligatoires.
          </div>

          {/* Grid 2 colonnes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Nom */}
            <Field label="Nom" required error={errors.nom}>
              <input
                id="user-nom"
                data-autofocus
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                autoComplete="off"
                className={`w-full rounded-xl border px-3 py-2 bg-white/80 dark:bg-slate-900/70
                  ${errors.nom ? "border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
                               : "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"}`}
                placeholder="Ex : DUPONT"
              />
            </Field>

            {/* Prénom */}
            <Field label="Prénom" required error={errors.prenom}>
              <input
                type="text"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                autoComplete="off"
                className={`w-full rounded-xl border px-3 py-2 bg-white/80 dark:bg-slate-900/70
                  ${errors.prenom ? "border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
                                  : "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"}`}
                placeholder="Ex : Marie"
              />
            </Field>

            {/* Email */}
            <Field label="Email" required error={errors.email} className="sm:col-span-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                className={`w-full rounded-xl border px-3 py-2 bg-white/80 dark:bg-slate-900/70
                  ${errors.email ? "border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
                                  : "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"}`}
                placeholder="exemple@domaine.fr"
              />
            </Field>

            {/* Rôle */}
            <Field label="Rôle" required error={errors.roleId} className="sm:col-span-2">
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className={`w-full rounded-xl border px-3 py-2 bg-white/80 dark:bg-slate-900/70
                  ${errors.roleId ? "border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
                                   : "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"}`}
              >
                <option value="">Sélectionner un rôle</option>
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Hint démo */}
          {demoMode && (
            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
              Mode démo : l’enregistrement est désactivé.
            </p>
          )}
        </form>
      </ModalBody>

      <ModalFooter>
        <Button
          text="Annuler"
          onClick={onClose}
          icon={FaTimes}
          type="button"
          variant="subtle"
          className="!rounded-xl"
        />
        <Button
          text={saving ? "Enregistrement…" : "Enregistrer"}
          icon={FaSave}
          type="submit"
          onClick={handleSubmit}
          disabled={demoMode || saving}
          loading={saving}
          className={`!rounded-xl ${demoMode ? "!opacity-60 !cursor-not-allowed" : ""}`}
        />
      </ModalFooter>
    </Modal>
  );
}

/* ====== Petit atome “Field” réutilisable ====== */
function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
        {label} {required ? <span className="text-pink-600">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
      {error ? (
        <p className="mt-1 text-xs text-rose-500">{error}</p>
      ) : null}
    </div>
  );
}
